import {
  PageConfigWithCharts,
  PageCategory,
  HourSelectorType,
} from '../../../shared/features/ng-redux/page/page.models';
import { ApplicationConfigWithPages } from '../../../shared/features/ng-redux/app/appConfig.models';

import {
  TimelineChartConfig,
  BarChartConfig,
  DonutChartConfig,
  StackedBarChartConfig,
  TableConfig,
  ChartTypeEnum,
  LineChartConfig,
  MostLeastEnum,
} from '../../../shared/models/chart.models';

import {
  DEVICE_LIST,
  DEVICE_BATTERY_USAGE,
  DEVICE_APP_USAGE,
  DEVICE_DATA_USAGE,
  DEVICE_WEBAPP_USAGE,
  DEVICE_LAST_KNOWN,
  DEVICE_SIGNAL_STRENGTH,
  DEFAULT_DEVICE_FILTER_BAR_CONFIG,
  SMART_BATTERY_SPOTLIGHT,
  CHART_UI_BAR_TOPN,
  CHART_UI_BAR_TOPN_MANY,
  DEVICE_WIFI,
  CHART_UI_DONUT_LEAST_MOST,
  WF_SDD_CHART_UI_BAR_TOPN,
  WF_CHART_UI_BAR_TOPN,
} from './app-mapper.constants';

import { buildChartOptions, findPageId, patchCard, patchChart, patchPage } from './app-mapper.utils';
import { ColorConstants, HighchartsConstant, QUALITY_COLORS } from '../../../../../../xs-colors';

import * as sddLoc from './app-mapper.sdd.loc';
import * as sbMapper from './app-mapper.smart-battery';
import { InsSelectionType, TableRenderEnum } from '@nx-fe/table';
import { CONSTANTS } from '../../../shared/localizations/dictionary';
import { ProfilePermissionEnum } from '../../../../../../../libs/services';

function patchDeviceListPage(page: PageConfigWithCharts) {
  page.claims = [
    ProfilePermissionEnum.ProfileViewBatteryStatusDashboard,
    ProfilePermissionEnum.ProfileViewWifiDashboard,
    ProfilePermissionEnum.ProfileViewDataUsageDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ProfilePermissionEnum.ProfileViewLastLocationDashboard,
    ProfilePermissionEnum.ProfileViewSignalStrengthDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
    ProfilePermissionEnum.ProfileViewWebAppUsageDashboard,
  ];
  page.filterBarVisible = true;
  page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
  page.pageCategory = PageCategory.DEVICE;

  patchChart(page.charts, 'device-list-table', (chart: TableConfig) => {
    chart.name = 'Ticket_List_Devices_Header';

    chart.selectionType = InsSelectionType.multiple;
    chart.selectionText = 'device_selected_label';
    chart.selectionTextPlural = 'device_selected_label';
    chart.rowActions = [
      {
        action: 'customAttributes',
        tooltip: 'custom_attr_title',
        icon: 'insight-icon-deviceinfo',
      },
    ];

    chart.filters = {
      sortAttribute: 'McDeviceId',
      sortDirection: 'desc',
      pageSize: 50,
      pageSkip: 0,
    };

    chart.tableAttributes = [
      {
        id: 'DeviceName',
        label: 'Column_Name_Device_Name',
        renderer: TableRenderEnum.LINK,
        primaryColumnSortId: 'DeviceName',
        rendererParams: {
          eventId: 'deviceClicked',
          icons: [
            {
              iconClass: 'insight-icon-type-DeviceAndroidForWork',
            },
          ],
        },
      },
      {
        id: 'McDeviceId',
        label: 'Column_Name_Device_Id',
        renderer: TableRenderEnum.STRING,
      },
      {
        id: 'Name',
        label: 'Column_Name_Device_Group',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'GroupPath',
        label: 'group_path_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'ManufacturerName',
        label: 'label_Manufacturer',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'ModelName',
        label: 'Column_Name_Model',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'OSVersion',
        label: 'Column_Name_Os_Version',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'OSVersionAsPaddedString',
      },
      {
        id: 'storageFreeLastKnown',
        label: 'availablestorage_label',
        renderer: TableRenderEnum.PROGRESS_BAR,
        iconClass: '',
        primaryColumnSortId: 'storageFreeLastKnown',
      },
      {
        id: 'MACAddress',
        label: 'label_MACAddress',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        shouldTranslate: true,
      },
    ];
  });
}

function patchDeviceSummaryPage(page: PageConfigWithCharts) {
  if (page.charts) {
    patchCard(page.charts, 'device-summary-batteryUsageCard', card => {
      card.icon = 'insight-icon-battery-usage';
      card.iconTooltip = 'app-mapper-sdd-device-summary-batteryUsageCard-tooltip';
      card.noDataIcon = 'insight-icon-nobatterydata';
    });
    patchCard(page.charts, 'device-summary-appUsageCard', card => {
      card.icon = 'insight-icon-application';
      card.iconTooltip = 'app-mapper-sdd-device-summary-appUsageCard-tooltip';
      card.noDataIcon = 'insight-icon-noappdata';
    });
    patchCard(page.charts, 'device-summary-dataUsageCard', card => {
      card.icon = 'insight-icon-datausage';
      card.iconTooltip = 'app-mapper-sdd-device-summary-dataUsageCard-tooltip';
      card.noDataIcon = 'insight-icon-nocellulardata';
    });
    patchCard(page.charts, 'device-summary-lastKnownCard', card => {
      card.icon = 'insight-icon-last-known';
      card.iconTooltip = 'app-mapper-sdd-device-summary-lastKnownCard-tooltip';
      card.noDataIcon = 'insight-icon-nolocation';
      card.cardStyle = ['card-1x2'];
    });
    patchCard(page.charts, 'device-summary-icuCard', card => {
      card.icon = 'insight-icon_intensivecare';
      card.iconTooltip = 'app-mapper-sdd-device-summary-icuCard-tooltip';
      card.noDataIcon = 'insight-icon-noicudata';
      card.chartHeight = 240;
    });
    patchCard(page.charts, 'device-summary-mcDetailsCard', card => {
      card.icon = 'insight-icon-device';
      card.iconTooltip = 'app-mapper-sdd-device-summary-mcDetailsCard-tooltip';
      card.cardStyle = ['static', 'card-1x3'];
    });
    patchCard(page.charts, 'device-summary-mcComplianceCard', card => {
      card.icon = 'insight-icon-activated-alt';
      card.iconTooltip = 'app-mapper-sdd-device-summary-mcComplianceCard-tooltip';
      card.cardStyle = ['static'];
    });
    patchCard(page.charts, 'device-summary-mcUserCard', card => {
      card.icon = 'insight-icon-user';
      card.iconTooltip = 'app-mapper-sdd-device-summary-mcUserCard-tooltip';
      card.cardStyle = ['static'];
    });
    patchCard(page.charts, 'device-summary-mcHardwareCard', card => {
      card.icon = 'insight-icon-type-DeviceAndroidGeneric';
      card.iconTooltip = 'app-mapper-sdd-device-summary-mcHardwareCard-tooltip';
      card.cardStyle = ['static'];
    });
  }

  page.icon = 'insight-icon-deviceinfo';
  page.name = 'app-mapper-sdd-insight-deviceinfo-name';
}

function patchDeviceBatteryPage(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewBatteryStatusDashboard];
  if (page.charts) {
    patchCard(page.charts, 'device-battery-overviewCard', card => {
      card.name = 'app_mapper_sdd_Overview';
    });

    patchCard<StackedBarChartConfig>(page.charts, 'device-battery-temperatureSeriesCard', chart => {
      chart.iconTooltip = 'sdd_battery_chart_temperature_series_tooltip';
      chart.chartHeight = 240;

      chart.name = 'Data_Usage_Charts_Category_Temperature_Series';
      chart.alpha = 10.2;
      chart.beta = 10.2;
      chart.scrollableX = true;
      chart.allowPointSelect = false;
      chart.stacking = 'percent';
      chart.dimension = {
        dimensionId: 'date',
        label: 'sdd_battery_chart_temperature_series_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.filter = {
        attribute: 'date',
      };
      chart.childDimension = {
        dimensionId: 'name',
        label: 'sdd_battery_chart_temperature_series_dimension_label',
      };
      chart.metric = {
        metricId: 'value',
        label: 'sdd_battery_chart_temperature_series_metric_label',
      };

      chart.tooltip = {
        label: 'sdd_battery_chart_temperature_series_tooltip_label',
        value: 'sdd_battery_chart_temperature_series_tooltip_value',
      };
      chart.fixedColors = [
        {
          label: 'sdd_battery_chart_temperature_normal_color_label',
          color: HighchartsConstant.batteryTemperatureBucket.normal,
        },
        {
          label: 'sdd_battery_chart_temperature_too_hot_color_label',
          color: HighchartsConstant.batteryTemperatureBucket.hot,
        },
        {
          label: 'sdd_battery_chart_temperature_too_cold_color_label',
          color: HighchartsConstant.batteryTemperatureBucket.cold,
        },
      ];
    });

    patchCard<BarChartConfig>(page.charts, 'device-battery-appDrainCard', chart => {
      chart.iconTooltip = 'sdd_battery_chart_app_drain_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_battery_chart_app_drain_name';

      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_battery_chart_app_drain_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'percentTotalAppDrain',
        label: 'sdd_battery_chart_app_drain_metric_label',
        format: '%(value).2f%%',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('xs_app_name_label', this.point.label, this.point.packageId)}`",
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value: "`${xsI18nPipe.transform('sdd_battery_chart_app_drain_tooltip_value', this.point.y)}`",
        valueArgs: ['sdd_battery_chart_app_drain_tooltip_value', 'this.point.y'],
      };

      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<DonutChartConfig>(page.charts, 'device-battery-chargePatternCard', chart => {
      chart.iconTooltip = 'app-mapper-sdd-device-battery-chargePatternCard-iconTooltip';
      chart.chartHeight = 240;

      chart.name = 'sdd_battery_charge_charge_pattern_name';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_battery_charge_charge_pattern_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_battery_charge_charge_pattern_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_battery_charge_charge_pattern_tooltip_value", this.point.y, (this.point.ratio * 100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalCount)}`',
        valueArgs: [
          'sdd_battery_charge_charge_pattern_tooltip_value',
          'this.point.y',
          'this.point.ratio',
          'this.point.totalCount',
        ],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_battery_chart_charge_pattern_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'count',
        label: 'sdd_battery_chart_charge_pattern_metric_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'chargePatternBucket',
      };
      chart.fixedColors = [
        {
          label: 'sdd_battery_chart_charge_pattern_spot_charged_color_label',
          color: HighchartsConstant.defaultBucket.average,
        },
        {
          label: 'sdd_battery_chart_charge_pattern_randomly_charged_color_label',
          color: QUALITY_COLORS.lime,
        },
        {
          label: 'sdd_battery_chart_charge_pattern_overcharged_color_label',
          color: HighchartsConstant.defaultBucket.bad,
        },
        {
          label: 'sdd_battery_chart_charge_pattern_optimally_charged_color_label',
          color: HighchartsConstant.defaultBucket.good,
        },
      ];
    });

    patchCard<BarChartConfig>(page.charts, 'device-battery-chargeLevelCard', chart => {
      chart.iconTooltip = 'app-mapper-sdd-device-battery-chargeLevelCard-iconTooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_battery_chart_charge_level_hour_name';

      chart.integerOnly = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.fixedColors = [
        {
          color: ColorConstants.sotiColors('gradient', 5),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-0-9Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 10),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-10-19Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 20),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-20-29Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 30),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-30-39Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 40),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-40-49Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 50),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-50-59Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 60),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-60-69Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 70),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-70-79Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 80),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-80-89Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 90),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-90-100Percent',
        },
      ];
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('sdd_battery_chart_battery_charge_level_hour_tooltip_label', xsI18nPipe.transform(this.point.hr), xsI18nPipe.transform(this.point.label))}`",
        labelArgs: [
          'sdd_battery_chart_battery_charge_level_hour_tooltip_label',
          'xsI18nPipe.transform(this.point.hr)',
          'xsI18nPipe.transform(this.point.label)',
        ],
        value:
          "`${xsI18nPipe.transform('sdd_battery_chart_battery_charge_level_hour_tooltip_value', this.point.y, (this.point.y/this.point.total*100).toFixed(2).replace(/\\.?0+$/,''), this.point.total)}`",
        valueArgs: [
          'sdd_battery_chart_battery_charge_level_hour_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.total)',
          'this.point.total',
        ],
      };
      chart.tag = {
        name: 'sdd_battery_chart_charge_level_hour_name',
        parameters: ['hour'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_battery_chart_charge_level_hour_dimension_label',
        noDataLabel: '{NO_DATA}',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_battery_chart_charge_level_hour_metric_label',
        format: '%(value).1f%%',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'batteryLevelAtHourBucket',
      };
      chart.filters = {
        hour: 8,
      };
      chart.uiSettings = [
        {
          id: 'hour',
          controlType: 'SELECTBOX',
          type: 'number',
          displayType: 'dropdown',
          options: [
            {
              value: 0,
              label: '0_hour',
            },
            {
              value: 1,
              label: '1_hour',
            },
            {
              value: 2,
              label: '2_hour',
            },
            {
              value: 3,
              label: '3_hour',
            },
            {
              value: 4,
              label: '4_hour',
            },
            {
              value: 5,
              label: '5_hour',
            },
            {
              value: 6,
              label: '6_hour',
            },
            {
              value: 7,
              label: '7_hour',
            },
            {
              value: 8,
              label: '8_hour',
            },
            {
              value: 9,
              label: '9_hour',
            },
            {
              value: 10,
              label: '10_hour',
            },
            {
              value: 11,
              label: '11_hour',
            },
            {
              value: 12,
              label: '12_hour',
            },
            {
              value: 13,
              label: '13_hour',
            },
            {
              value: 14,
              label: '14_hour',
            },
            {
              value: 15,
              label: '15_hour',
            },
            {
              value: 16,
              label: '16_hour',
            },
            {
              value: 17,
              label: '17_hour',
            },
            {
              value: 18,
              label: '18_hour',
            },
            {
              value: 19,
              label: '19_hour',
            },
            {
              value: 20,
              label: '20_hour',
            },
            {
              value: 21,
              label: '21_hour',
            },
            {
              value: 22,
              label: '22_hour',
            },
            {
              value: 23,
              label: '23_hour',
            },
          ],
        },
      ];
    });

    patchCard<DonutChartConfig>(page.charts, 'device-battery-temperatureCard', chart => {
      chart.iconTooltip = 'sdd_battery_chart_temperature_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_battery_chart_temperature_name';

      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_battery_temperature_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_battery_temperature_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_battery_temperature_tooltip_value", this.point.y, (this.point.y/this.point.total*100).toFixed(2).replace(/\\.?0+$/,\'\') , this.point.total)}`',
        valueArgs: [
          'sdd_battery_temperature_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.total)',
          'this.point.total',
        ],
      };
      chart.fixedColors = [
        {
          label: 'sdd_battery_chart_temperature_large_fluctuations_color_label',
          color: HighchartsConstant.batteryTemperatureBucket.both,
        },
        {
          label: 'sdd_battery_chart_temperature_too_cold_color_label',
          color: HighchartsConstant.batteryTemperatureBucket.cold,
        },
        {
          label: 'sdd_battery_chart_temperature_too_hot_color_label',
          color: HighchartsConstant.batteryTemperatureBucket.hot,
        },
        {
          label: 'sdd_battery_chart_temperature_normal_color_label',
          color: HighchartsConstant.batteryTemperatureBucket.normal,
        },
      ];
      chart.uiSettings = [
        {
          id: 'batteryTemperature',
          controlType: 'THRESHOLD',
          type: 'number',
          displayType: 'dropdown',
          name: 'sdd_battery_chart_temperature_settings_threshold_message',
          options: [],
        },
      ];
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_battery_chart_temperature_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_battery_chart_temperature_metric_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'tempftusBucket',
      };
    });

    patchCard<TimelineChartConfig>(page.charts, 'device-battery-chargeLevetTS', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_battery_level_ts_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_battery_level_ts_name';
      chart.integerOnly = true;

      chart.dimension = {
        dimensionId: 'CollectedTime',
        label: 'sdd_battery_level_ts_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'TotalUsage',
        label: 'sdd_battery_level_ts_metric_label',
      };
      chart.tooltip = {
        label: 'sdd_battery_level_ts_tooltip_label',
        value: 'sdd_battery_level_ts_tooltip_value',
      };
      chart.xMinRange = 1000 * 60 * 60;
      chart.yMax = 100;
      chart.legendDisabled = true;
    });
  }

  page.applicationType = ['battery'];
  page.icon = 'insight-icon-battery-usage';
  page.name = 'Battery_Status_Charts_Category_Header';

  page.predefinedCharts = [
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Summary',
        icon: 'insight-icon-battery-usage',
        tooltip: 'Battery_Status_Charts_Category_header_Summary_Information_Tooltip',
      },
      charts: [buildChartOptions(page.charts, 'device-battery-overviewCard', 'app_mapper_sdd_Overview')],
    },
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Time_Series',
        icon: 'insight-icon-linechart',
        tooltip: 'Battery_Status_Charts_Category_Header_Time_Series_Tooltip',
      },
      charts: [
        // buildChartOptions(
        //   page.charts,
        //   'device-battery-sohSeriesCard',
        //   'Data_Usage_Charts_Category_Remainging_Battery_Capacity'
        // ),

        buildChartOptions(
          page.charts,
          'device-battery-temperatureSeriesCard',
          'Data_Usage_Charts_Category_Temperature_Series'
        ),
        buildChartOptions(page.charts, 'device-battery-chargeLevetTS', 'sdd_battery_level_ts_name'),
      ],
    },
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header',
        icon: 'insight-icon-battery-usage',
        tooltip: 'Battery_Status_Charts_Category_Header_Battery_Status_Tooltip',
      },
      charts: [
        buildChartOptions(
          page.charts,
          'device-battery-appDrainCard',
          'Device_Charts_Category_Chart_Caption_Top_App_Drain'
        ),
        buildChartOptions(
          page.charts,
          'device-battery-temperatureCard',
          'Device_Charts_Category_Chart_Caption_Top_Battery_Temperature'
        ),
      ],
    },
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Charging',
        icon: 'insight-icon-battery-charging',
        tooltip: 'Battery_Status_Charts_Category_Header_Charging_Tooltip',
      },
      charts: [
        buildChartOptions(
          page.charts,
          'device-battery-chargePatternCard',
          'Battery_Status_Charts_Category_Charge_Pattern'
        ),
        buildChartOptions(
          page.charts,
          'device-battery-chargeLevelCard',
          'Device_Charts_Category_Chart_Caption_Charge_Level'
        ),
      ],
    },
  ];
}

function patchDeviceAppPage(page: PageConfigWithCharts) {
  page.claims = [
    ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
  ];
  if (page.charts) {
    patchCard<DonutChartConfig>(page.charts, 'device-app-appClassCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_app_usage_categories_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_app_usage_categories_name';

      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_app_usage_categories_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_app_usage_categories_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_app_usage_categories_tooltip_value", this.point.y, (this.point.ratio).toFixed(2).replace(/\\.?0+$/,\'\') , this.point.totalCount)}`',
        valueArgs: [
          'sdd_app_usage_categories_tooltip_value',
          'this.point.y',
          'this.point.ratio',
          'this.point.totalCount',
        ],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_app_usage_categories_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_categories_metric_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'type',
      };
    });

    patchCard<DonutChartConfig>(page.charts, 'device-app-storageCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_app_usage_storage_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_app_usage_storage_name';

      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_app_usage_storage_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_app_usage_storage_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_app_usage_storage_tooltip_value", this.point.y, (this.point.ratio).toFixed(2).replace(/\\.?0+$/,\'\') , this.point.totalCount)}`',
        valueArgs: ['sdd_app_usage_storage_tooltip_value', 'this.point.y', 'this.point.ratio', 'this.point.totalCount'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_app_usage_storage_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_storage_metric_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'storageBucket',
      };
      chart.fixedColors = [
        {
          label: '{StorageSize_6}',
          color: HighchartsConstant.appUsageStaticBucket.other,
        },
        {
          label: '{StorageSize_5}',
          color: HighchartsConstant.appUsageStaticBucket.item5,
        },
        {
          label: '{StorageSize_4}',
          color: HighchartsConstant.appUsageStaticBucket.item4,
        },
        {
          label: '{StorageSize_3}',
          color: HighchartsConstant.appUsageStaticBucket.item3,
        },
        {
          label: '{StorageSize_2}',
          color: HighchartsConstant.appUsageStaticBucket.item2,
        },
        {
          label: '{StorageSize_1}',
          color: HighchartsConstant.appUsageStaticBucket.item1,
        },
      ];
    });

    patchCard<BarChartConfig>(page.charts, 'device-app-unopenedCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_app_usage_unopened_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_app_usage_unopened_name';

      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_app_usage_unopened_tooltip_label", this.point.label)}`',
        labelArgs: ['sdd_app_usage_unopened_tooltip_label', 'this.point.label'],
        value:
          '`${xsI18nPipe.transform("sdd_app_usage_unopened_tooltip_value", this.point.y, (this.point.y/this.point.total*100).toFixed(2).replace(/\\.?0+$/,\'\') , this.point.total)}`',
        valueArgs: [
          'sdd_app_usage_unopened_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.total)',
          'this.point.total',
        ],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_dimension_application_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_metric_installs_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageDeviceId',
      };
      chart.tag = {
        name: 'sdd_app_usage_unopened_name',
        parameters: ['topN'],
      };
      chart.filters = {
        topN: 5,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY];
    });

    patchCard<BarChartConfig>(page.charts, 'device-app-visitedCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_app_usage_visited_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_app_usage_visited_name';

      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("sdd_app_usage_visited_tooltip_value", this.point.y, this.point.visitRatio.toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalVisits)}`',
        valueArgs: [
          'sdd_app_usage_visited_tooltip_value',
          'this.point.y',
          'this.point.visitRatio',
          'this.point.totalVisits',
        ],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_dimension_application_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_visited_metric_visits_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageDeviceId',
      };
      chart.tag = {
        name: 'sdd_app_usage_visited_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-app-foregroundCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_app_usage_foreground_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_app_usage_foreground_name';

      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform(\"sdd_app_usage_foreground_tooltip_value\", this.point.yUnit, (this.point.ratio).toFixed(2).replace(/\\.?0+$/,''), this.point.total.toFixed(2).replace(/\\.?0+$/,''), this.point.maxUnit)}`",
        valueArgs: [
          'sdd_app_usage_foreground_tooltip_value',
          'this.point.yUnit',
          'this.point.ratio',
          'this.point.total',
          'this.point.maxUnit',
        ],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_dimension_application_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_foreground_metric_label',
        format: '%(value).2f %(yAxisUnit)s',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageDeviceId',
      };
      chart.tag = {
        name: 'sdd_app_usage_foreground_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-app-versionCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_app_usage_versions_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_app_usage_versions_name';

      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label:
          '`${xsI18nPipe.transform("sdd_app_usage_version_tooltip_label", this.point.label, this.point.versionName)}`',
        labelArgs: ['sdd_app_usage_version_tooltip_label', 'this.point.label', 'this.point.versionName'],
        value: '`${xsI18nPipe.transform("sdd_app_usage_version_tooltip_value", this.point.packageId)}`',
        valueArgs: ['sdd_app_usage_version_tooltip_value', 'this.point.packageId'],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_dimension_application_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageId',
        childAttribute: 'versionName',
      };
      chart.tag = {
        name: 'sdd_app_usage_versions_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-app-trendingInstallCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_app_usage_trending_installs_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_app_usage_trending_installs_name';
      // This was on the config for some reason
      // chart.applicationTypes: ["appusage"];

      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("sdd_app_usage_trending_installs_tooltip_value", this.point.y, this.point.totalInstalls, (this.point.installRatio).toFixed(2).replace(/\\.?0+$/,\'\'))}`',
        valueArgs: [
          'sdd_app_usage_trending_installs_tooltip_value',
          'this.point.y',
          'this.point.totalInstalls',
          'this.point.installRatio',
        ],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_dimension_application_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_metric_installs_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageDeviceId',
      };
      chart.tag = {
        name: 'sdd_app_usage_trending_installs_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'device-app-list-tableCard', (chart: TableConfig) => {
      chart.name = 'as_table_app_name';
      chart.elementType = 'table';

      chart.filters = {
        sortAttribute: 'name',
        sortDirection: 'asc',
        pageSize: 50,
        pageSkip: 0,
      };

      chart.tableAttributes = [
        {
          id: 'name',
          label: 'xs_name_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'appClicked',
          },
        },
        {
          id: 'id',
          label: 'as_table_app_column_id_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'category',
          label: 'as_table_app_column_category_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'visits',
          label: 'sdd_app_usage_visited_metric_visits_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'storage',
          label: 'as_table_app_column_storage_size_label',
          renderer: TableRenderEnum.STRING,
        },
      ];
    });
    // patchCard<TimelineChartConfig>(page.charts, 'device-app-eventsCard', chart => {
    //   chart.icon = 'insight-icon-moreaction';
    //   chart.iconTooltip =
    //     'The chart displays the relevant app events occurred for the single device within the specified date range.';
    //   chart.chartHeight = 240;
    // }
  }
  page.applicationType = ['appusage'];
  page.icon = 'insight-icon-application';
  page.name = 'App_Usage_Charts_Category_Header_App_Usage';

  page.predefinedCharts = [
    {
      category: {
        name: 'app-mapper-sdd-device-predefined-header-app-deployment',
        icon: '',
        tooltip: 'App_Usage_Charts_Category_Header_Usage_Tooltip',
      },
      charts: [
        buildChartOptions(
          page.charts,
          'device-app-appClassCard',
          'app-mapper-sdd-device-battery-Configuration-appClassCard'
        ),
        buildChartOptions(
          page.charts,
          'device-app-versionCard',
          'app-mapper-sdd-device-battery-Configuration-versionCard'
        ),
        buildChartOptions(
          page.charts,
          'device-app-trendingInstallCard',
          'app-mapper-sdd-device-battery-Configuration-trendingInstallCard'
        ),
      ],
    },
    {
      category: {
        name: 'app-mapper-sdd-device-battery-Usage',
        icon: '',
        tooltip: 'app-mapper-sdd-device-battery-Usage-tooltip',
      },
      charts: [
        buildChartOptions(page.charts, 'device-app-storageCard', 'App_Usage_Charts_Category_App__Storage_Sizes'),
        buildChartOptions(page.charts, 'device-app-unopenedCard', 'App_Usage_Charts_Category_Apps_Unopened'),
        buildChartOptions(page.charts, 'device-app-visitedCard', 'App_Usage_Charts_Category_Top_Visited_App'),
        buildChartOptions(page.charts, 'device-app-foregroundCard', 'App_Usage_Charts_Category_Foreground_Usage'),
      ],
    },
  ];
}

function patchDeviceDataPage(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewDataUsageDashboard];
  if (page.charts) {
    patchChart(page.charts, 'ddTotalUsageTS', (chart: LineChartConfig) => {
      chart.fullName = 'sdd_data_chart_data_usage_time_fullname';
      chart.name = 'sdd_data_chart_data_usage_time_name';
      chart.iconTooltip = 'sdd_data_chart_data_usage_time_icon_tooltip';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_chart_usage_time__metric_label',
      };
      chart.tag = {
        name: 'sdd_data_chart_data_usage_time_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_chart_data_usage_time_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    patchCard<BarChartConfig>(page.charts, 'device-data-appUsageCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_app_usage_icon_tooltip';
      chart.name = 'sdd_data_usage_app_usage_name';
      chart.layout = '50p';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("sdd_data_usage_app_usage_tooltip_value", this.point.ytip, (this.point.y / this.point.total * 100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalTip)}`',
        valueArgs: [
          'sdd_data_usage_app_usage_tooltip_value',
          'this.point.ytip',
          'percentage(this.point.y, this.point.total)',
          'this.point.totalTip',
        ],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_dimension_application_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_app_usage_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'appUsage',
        label: 'data_usage_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageId',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-data-browserUsageCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_browser_icon_tooltip';
      chart.layout = '50p';
      chart.name = 'sdd_data_usage_browser_name';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("sdd_data_usage_browser_tooltip_value", this.point.ytip, (this.point.y / this.point.total * 100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalTip)}`',
        valueArgs: [
          'sdd_data_usage_browser_tooltip_value',
          'this.point.ytip',
          'percentage(this.point.y, this.point.total)',
          'this.point.totalTip',
        ],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_data_usage_browser_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'appUsage',
        label: 'data_usage_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageId',
      };
      chart.tag = {
        name: 'sdd_data_usage_app_usage_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'device-data-totalUsageCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_by_connection_type_icon_tooltip';
      chart.name = 'sdd_data_usage_by_connection_type_name';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'data_chart_time_dimension_label',
        noDataLabel: 'no_data_label',
      };

      chart.timelineColors = [
        {
          label: 'sdd_data_usage_total_total_usage',
          color: HighchartsConstant.dataUsageTypeBucket.total,
        },
        {
          label: 'sdd_data_usage_total_wifi_usage',
          color: HighchartsConstant.dataUsageTypeBucket.wifi,
        },
        {
          label: 'sdd_data_usage_total_ether_usage',
          color: HighchartsConstant.dataUsageTypeBucket.ether,
        },
        {
          label: 'sdd_data_usage_total_cellular_usage',
          color: HighchartsConstant.dataUsageTypeBucket.cellular,
        },
        {
          label: 'sdd_data_usage_total_roaming_usage',
          color: HighchartsConstant.dataUsageTypeBucket.roaming,
        },
      ];
      chart.multiTimelineMetric = [
        {
          metricId: 'allY',
          label: 'sdd_data_usage_by_connection_type',
        },
        { metricId: 'wifiY', label: 'sdd_data_usage_by_connection_type_wifi_usage' },
        { metricId: 'etherY', label: 'sdd_data_usage_by_connection_type_ether_usage' },
        { metricId: 'cellY', label: 'sdd_data_usage_by_connection_type_cellular_usage' },
        { metricId: 'roamY', label: 'sdd_data_usage_by_connection_type_roam_usage' },
      ];
      chart.metric = {
        metricId: '',
        label: 'data_usage_label',
      };
      chart.tooltip = {
        label: 'sdd_data_usage_by_connection_type_tooltip_label',
        value: 'sdd_data_usage_by_connection_type_tooltip_value',
        dateTime: 'sdd_data_usage_by_connection_type_tooltip_date_time',
      };
    });

    patchCard<DonutChartConfig>(page.charts, 'device-data-uploadDownloadCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_upload_download_icon_tooltip';
      chart.name = 'sdd_data_usage_upload_download_name';
      chart.layout = '50p';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_data_usage_upload_download_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_data_usage_upload_download_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_data_usage_upload_download_tooltip_value", this.point.yStr, (this.total ? (" (" + (this.point.y / this.total * 100).toFixed(2).replace(/\\.?0+$/,\'\') + "%)") : ""), this.point.totalStr)}`',
        valueArgs: [
          'sdd_data_usage_upload_download_tooltip_value',
          'this.point.yStr',
          'percentage(this.point.y, this.point.total)',
          'this.point.totalStr',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_data_usage_upload_download_name',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_upload_download_data_use_label',
      };
      chart.fixedColors = [
        {
          label: '{DATA_UPLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item1,
        },
        {
          label: '{DATA_DOWNLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item2,
        },
      ];
    });

    patchChart(page.charts, 'ddAvgConnTimeTS', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_avg_conn_time_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_avg_conn_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'sdd_data_usage_avg_conn_time_ts_metric_label',
      };
      chart.metrics = [
        {
          id: 'timeCell',
          timeFormatValueId: 'hourMinute1',
          countId: 'devCell',
        },
        {
          id: 'timeWifi',
          timeFormatValueId: 'hourMinute2',
          countId: 'devWiFi',
        },
      ];
      chart.tag = {
        name: 'sdd_data_usage_avg_conn_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_avg_conn_time_ts_tooltip_value',
      };
      chart.legendDisabled = false;
      chart.legends = [
        'sdd_data_usage_avg_conn_time_ts_legend_cellular',
        'sdd_data_usage_avg_conn_time_ts_legend_wifi',
      ];
      chart.formatDate = true;
      chart.formatTime = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'ddRoamUsageTS', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_usage_roaming_usage_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_roaming_usage_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_roaming_usage_ts_metric_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_roaming_usage_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_roaming_usage_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'ddRoamTimeTS', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_usage_roaming_time_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_roaming_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_roaming_time_ts_metric_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_roaming_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_roaming_time_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'ddTetherUsageTS', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_usage_tether_usage_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_tether_data_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_tether_data_ts_metric_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_tether_data_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_tether_data_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'ddVpnTimeTS', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_usage_vpn_time_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_vpn_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_vpn_time_ts_metric_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_vpn_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_vpn_time_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.formatTime = true;
      chart.integerOnly = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });
  }

  page.applicationType = ['datausage'];
  page.icon = 'insight-icon-datausage';
  page.name = 'Device_Filter_Data_Usage';

  page.predefinedCharts = [
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Time_Series',
        icon: 'insight-icon-linechart',
        tooltip: 'Battery_Status_Charts_Category_Header_Time_Series_Tooltip',
      },
      charts: [
        buildChartOptions(page.charts, 'ddTotalUsageTS', 'sdd_data_predefined_chart_total_usage_ts'),
        buildChartOptions(
          page.charts,
          'device-data-totalUsageCard',
          'sdd_data_predefined_chart_data_usage_by_conn_type'
        ),
        buildChartOptions(page.charts, 'ddAvgConnTimeTS', 'sdd_data_predefined_chart_avg_connection_time_ts'),
        buildChartOptions(page.charts, 'ddRoamUsageTS', 'sdd_data_predefined_chart_roam_usage_ts'),
        buildChartOptions(page.charts, 'ddRoamTimeTS', 'sdd_data_predefined_chart_roam_time_ts'),
        buildChartOptions(page.charts, 'ddTetherUsageTS', 'sdd_data_predefined_chart_tether_usage_ts'),
        buildChartOptions(page.charts, 'ddVpnTimeTS', 'sdd_data_predefined_chart_vpn_time_ts'),
      ],
    },
    {
      category: {
        name: 'Data_Usage_Charts_Category_Header_Usage',
        icon: '',
        tooltip: 'Data_Usage_Charts_Category_Header_Usage_Tooltip',
      },
      charts: [
        buildChartOptions(page.charts, 'device-data-appUsageCard', 'Data_Usage_Charts_Category_Top_Apps_Data_Usage'),
        buildChartOptions(
          page.charts,
          'device-data-browserUsageCard',
          'Data_Usage_Charts_Category_Top_Browsers_Data_Usage'
        ),
        buildChartOptions(page.charts, 'device-data-uploadDownloadCard', 'Data_Usage_Charts_Category_Upload'),
      ],
    },
  ];
}

function patchDeviceWebAppPage(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewWebAppUsageDashboard];
  if (page.charts) {
    patchCard<DonutChartConfig>(page.charts, 'device-webapp-appClassCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_usage_categories_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_webapp_usage_categories_name';

      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_webapp_usage_categories_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_webapp_usage_categories_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_webapp_usage_categories_tooltip_value", this.point.y, (this.point.ratio).toFixed(2).replace(/\\.?0+$/,\'\') , this.point.totalCount)}`',
        valueArgs: [
          'sdd_webapp_usage_categories_tooltip_value',
          'this.point.y',
          'this.point.ratio',
          'this.point.totalCount',
        ],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_app_usage_categories_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_categories_metric_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'type',
      };
    });

    patchCard<BarChartConfig>(page.charts, 'device-webapp-topVisitedCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_usage_visited_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_webapp_usage_visited_name';

      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_webapp_usage_Web_App_label", this.point.label)}`',
        labelArgs: ['sdd_webapp_usage_Web_App_label', 'this.point.label'],
        value:
          '`${xsI18nPipe.transform("sdd_webapp_usage_visited_tooltip_value", this.point.y, this.point.totalVisits)}`',
        valueArgs: ['sdd_webapp_usage_visited_tooltip_value', 'this.point.y', 'this.point.totalVisits'],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_webapp_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'webapp_chart_visits_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageDeviceId',
      };
      chart.tag = {
        name: 'sdd_webapp_usage_visited_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'device-webapp-visitTimeline', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_visitTimeline_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_webapp_visitTimeline_name';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };
      chart.timelineColor = ColorConstants.sotiColors('yellow', 90);
      chart.metric = {
        metricId: 'metric',
        label: 'webapp_chart_visits_label',
      };
      chart.tooltip = {
        label: 'web_as_visitTimeline_tooltip_label',
        value: 'sdd_webapp_usage_visitTimeline_tooltip_value',
        dateTime: 'as_foreground_usage_tooltip_date_time',
      };
    });

    patchCard<BarChartConfig>(page.charts, 'device-webapp-topForegroundCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_usage_foreground_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_webapp_usage_foreground_name';

      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_webapp_usage_Web_App_label", this.point.label)}`',
        labelArgs: ['sdd_webapp_usage_Web_App_label', 'this.point.label'],
        value: '`${xsI18nPipe.transform("sdd_webapp_usage_foreground_tooltip_value", this.point.yUnit)}`',
        valueArgs: ['sdd_webapp_usage_foreground_tooltip_value', 'this.point.yUnit'],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_webapp_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_app_usage_foreground_metric_label',
        format: '%(value).2f %(yAxisUnit)s',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageDeviceId',
      };
      chart.tag = {
        name: 'sdd_webapp_usage_foreground_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'device-webapp-foregroundTimeline', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_foregroundTimeline_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'webapp_chart_foreground_time_name';

      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };

      chart.timelineColor = ColorConstants.sotiColors('green', 70);
      chart.metric = {
        metricId: 'metric',
        label: 'webapp_chart_foreground_label',
      };
      chart.tooltip = {
        label: 'as_foreground_usage_total_tooltip_label',
        value: 'sdd_webapp_usage_visitTimeline_tooltip_value',
        dateTime: 'as_foreground_usage_tooltip_date_time',
      };
    });

    patchCard<BarChartConfig>(page.charts, 'device-webapp-topClientErrorCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_topClientError_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'webapp_topClientError';
      chart.integerOnly = true;
      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("sdd_webapp_errors_tooltip_value", this.point.y, this.point.webAppCount)}`',
        valueArgs: ['sdd_webapp_errors_tooltip_value', 'this.point.y', 'this.point.webAppCount'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'error_code_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'number_of_errors_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'filter',
      };
      chart.tag = {
        name: 'webapp_topClientError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-webapp-topServerErrorCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_topServerError_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'webapp_topServerError';
      chart.integerOnly = true;
      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("sdd_webapp_errors_tooltip_value", this.point.y, this.point.webAppCount)}`',
        valueArgs: ['sdd_webapp_errors_tooltip_value', 'this.point.y', 'this.point.webAppCount'],
      };
      chart.dimension = {
        dimensionId: 'filter',
        label: 'error_code_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'number_of_errors_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'filter',
      };
      chart.tag = {
        name: 'webapp_topServerError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-webapp-topWebErrorCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_topWebError_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'webapp_topWebViewError';
      chart.integerOnly = true;
      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("sdd_webapp_errors_tooltip_value", this.point.y, this.point.webAppCount)}`',
        valueArgs: ['sdd_webapp_errors_tooltip_value', 'this.point.y', 'this.point.webAppCount'],
      };
      chart.dimension = {
        dimensionId: 'filter',
        label: 'error_code_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'number_of_errors_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'filter',
      };
      chart.tag = {
        name: 'webapp_topWebViewError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-webapp-topSSLErrorCard', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_topSSLError_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'webapp_topSSLError';
      chart.integerOnly = true;
      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("sdd_webapp_errors_tooltip_value", this.point.y, this.point.webAppCount)}`',
        valueArgs: ['sdd_webapp_errors_tooltip_value', 'this.point.y', 'this.point.webAppCount'],
      };
      chart.dimension = {
        dimensionId: 'filter',
        label: 'error_code_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'number_of_errors_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'filter',
      };
      chart.tag = {
        name: 'webapp_topSSLError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'device-webapp-errorsTimeline', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_webapp_errorsTimeline_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'webapp_chart_error_codes_name';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };

      chart.timelineColors = [
        {
          label: 'client_error_label',
          color: ColorConstants.sotiColors('yellow', 90),
        },
        {
          label: 'server_error_label',
          color: ColorConstants.sotiColors('cerise', 5),
        },
        {
          label: 'webview_error_label',
          color: ColorConstants.sotiColors('blue', 70),
        },
        {
          label: 'ssl_error_label',
          color: ColorConstants.sotiColors('green', 60),
        },
      ];
      chart.multiTimelineMetric = [
        { metricId: 'clientErrorY', label: 'webapp_client_errors' },
        { metricId: 'serverErrorY', label: 'webapp_server_errors' },
        { metricId: 'webViewErrorY', label: 'webapp_webView_errors' },
        { metricId: 'sslErrorY', label: 'webapp_ssl_errors' },
      ];
      chart.metric = {
        metricId: '',
        label: 'number_of_errors_label',
      };
      chart.tooltip = {
        label: 'webapp_error_timeline_tooltip_label',
        value: 'sdd_webapp_error_timeline_tooltip_value',
        dateTime: 'sdd_data_usage_total_tooltip_date_time',
      };
    });

    patchChart(page.charts, 'device-webapp-list-tableCard', (chart: TableConfig) => {
      chart.name = 'as_table_app_name';
      chart.elementType = 'table';

      chart.filters = {
        sortAttribute: 'webAppName',
        sortDirection: 'asc',
        pageSize: 50,
        pageSkip: 0,
      };

      chart.tableAttributes = [
        {
          id: 'webAppName',
          label: 'webapp_table_app_column_name_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'appClicked',
            eventParams: {
              surfApp: true,
            },
          },
        },
        {
          id: 'category',
          label: 'webapp_category_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'visits',
          label: 'webapp_visit_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'consumedTime',
          label: 'foreground_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'clientErrors',
          label: 'client_error_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'serverErrors',
          label: 'server_error_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'webViewErrors',
          label: 'webview_error_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'sslErrors',
          label: 'ssl_error_label',
          renderer: TableRenderEnum.STRING,
        },
      ];
    });
  }
  page.applicationType = ['webappusage'];
  page.icon = 'insight-icon-appusage';
  page.name = 'Device_Filter_Web_App_Usage';

  page.predefinedCharts = [
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Time_Series',
        icon: 'insight-icon-linechart',
        tooltip: 'Battery_Status_Charts_Category_Header_Time_Series_Tooltip',
      },
      charts: [
        buildChartOptions(page.charts, 'device-webapp-visitTimeline', 'sdd_webapp_visitTimeline_name'),
        buildChartOptions(page.charts, 'device-webapp-foregroundTimeline', 'webapp_chart_foreground_time_name'),
        buildChartOptions(page.charts, 'device-webapp-errorsTimeline', 'webapp_error_label'),
      ],
    },
    {
      category: {
        name: 'Data_Usage_Charts_Category_Header_Usage',
        icon: '',
        tooltip: 'Web_App_Usage_Charts_Category_Header_Usage_Tooltip',
      },
      charts: [
        buildChartOptions(page.charts, 'device-webapp-appClassCard', 'sdd_webapp_usage_categories_name'),
        buildChartOptions(page.charts, 'device-webapp-topVisitedCard', 'webapp_Usage_Charts_Category_Top_Visited_App'),
        buildChartOptions(
          page.charts,
          'device-webapp-topForegroundCard',
          'webapp_Usage_Charts_Category_Top_Foreground_Usage'
        ),
        buildChartOptions(
          page.charts,
          'device-webapp-topClientErrorCard',
          'webapp_Usage_Charts_Category_Top_Client_Error'
        ),
        buildChartOptions(
          page.charts,
          'device-webapp-topServerErrorCard',
          'webapp_Usage_Charts_Category_Top_Server_Error'
        ),
        buildChartOptions(
          page.charts,
          'device-webapp-topWebErrorCard',
          'webapp_Usage_Charts_Category_Top_WebView_Error'
        ),
        buildChartOptions(page.charts, 'device-webapp-topSSLErrorCard', 'webapp_Usage_Charts_Category_Top_SSL_Error'),
      ],
    },
  ];
}
function patchDeviceWifiPage(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewWifiDashboard];
  page.multiHourSelector = true;
  if (page.charts) {
    patchCard<DonutChartConfig>(page.charts, 'dwf-signalstrength', chart => {
      chart.iconTooltip = 'sdd_wf_signal_strength_icon_tooltip';
      chart.name = 'sdd_wf_signal_strength_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('sdd_data_usage_signal_tooltip_label', this.point.name)}`",
        labelArgs: ['sdd_data_usage_signal_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_data_usage_signal_tooltip_value", this.point.y, (100.0 * this.point.y / this.point.totalCount).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalCount)}`',
        valueArgs: [
          'sdd_data_usage_signal_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.totalCount)',
          'this.point.totalCount',
        ],
      };
      chart.uiSettings = [
        {
          id: 'WifiSignalStrength',
          controlType: 'THRESHOLD',
          type: 'number',
          displayType: 'dropdown',
          name: 'select_signal_strength_wifi',
          options: [],
        },
      ];
      chart.fixedColors = [
        {
          label: '{SignalStrength_4}',
          color: HighchartsConstant.signalStrengthBucket.nodata,
        },
        {
          label: '{SignalStrength_0}',
          color: HighchartsConstant.signalStrengthBucket.nosignal,
        },
        {
          label: '{SignalStrength_3}',
          color: HighchartsConstant.signalStrengthBucket.bad,
        },
        {
          label: '{SignalStrength_2}',
          color: HighchartsConstant.signalStrengthBucket.average,
        },
        {
          label: '{SignalStrength_1}',
          color: HighchartsConstant.signalStrengthBucket.good,
        },
      ];
    });
    patchCard<BarChartConfig>(page.charts, 'dwf-freqband', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wf_freq_band_icon_tooltip';
      chart.name = 'sdd_wf_freq_band_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.integerOnly = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('wf-freq-band-name', xsI18nPipe.transform(this.point.name))}`",
        labelArgs: ['wf-freq-band-name', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('sdd_data_usage_signal_tooltip_value', this.point.y, (100.0 * this.point.y / this.point.totalCount).toFixed(2).replace(/\\.?0+$/,''), this.point.totalCount)}`",
        valueArgs: [
          'sdd_data_usage_signal_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.totalCount)',
          'this.point.totalCount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_wf_freq_band_name',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'readings_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
    });
    patchCard<LineChartConfig>(page.charts, 'dwf-signalstrength-ts', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wf_avg_signal_strength_icon_tooltip';
      chart.name = 'sdd_wf_avg_signal_strength_name';
      chart.type = ChartTypeEnum.LINE;
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_avg_signal_strength_dbm',
      };
      chart.tag = {
        name: 'data_chart_data_usage_time_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_wifi_signal_strength_ts_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.allowNegativeValue = true;
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    patchCard<DonutChartConfig>(page.charts, 'dwf-usage-updown', chart => {
      chart.iconTooltip = 'sdd_wifi_usage_up_down_icon_tooltip';
      chart.name = 'sdd_wifi_usage_up_down_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('sdd_wifi_usage_up_down_tooltip_label', this.point.name)}`",
        labelArgs: ['sdd_wifi_usage_up_down_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_wifi_usage_up_down_tooltip_value", this.point.yUnit, this.point.usageRatio, this.point.totalUsageUnit)}`',
        valueArgs: [
          'sdd_wifi_usage_up_down_tooltip_value',
          'this.point.yUnit',
          'this.point.usageRatio',
          'this.point.totalUsageUnit',
        ],
      };
      chart.fixedColors = [
        {
          label: '{DATA_DOWNLOAD}',
          color: HighchartsConstant.signalStrengthBucket.good,
        },
        {
          label: '{DATA_UPLOAD}',
          color: QUALITY_COLORS.blue,
        },
      ];
    });
    patchCard<BarChartConfig>(page.charts, 'dwf-usage-app', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wifi_app_data_usage_icon_tooltip';
      chart.name = 'sdd_wifi_app_data_usage_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('sdd_wifi_app_data_usage_tooltip_label', this.point.name)}`",
        labelArgs: ['sdd_wifi_app_data_usage_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_wifi_app_data_usage_tooltip_value", this.point.yUnit, this.point.usageRatio, this.point.totalUsageUnit)}`',
        valueArgs: [
          'sdd_wifi_app_data_usage_tooltip_value',
          'this.point.yUnit',
          'this.point.usageRatio',
          'this.point.totalUsageUnit',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'Application',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'sdd_wifi_app_data_usage_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.metric = {
        metricId: 'y',
        label: 'Data Used',
        metricFormat: 'data',
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchCard<LineChartConfig>(page.charts, 'dwf-usage-ts', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_total_total_usage_icon_tooltip';
      chart.name = 'sdd_data_usage_total_total_usage';
      chart.type = ChartTypeEnum.LINE;
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_wf_total_data_usage_metric_label',
        metricFormat: 'data',
      };
      chart.tag = {
        name: 'data_chart_data_usage_time_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_wifi_total_data_usage_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    patchCard<LineChartConfig>(page.charts, 'dwf-metered-ts', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wifi_time_on_metered_unmetered_icon_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_wifi_time_on_metered_unmetered_name';
      chart.type = ChartTypeEnum.LINE;
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'wf_metered_unmetered_connections_avg_time',
      };
      chart.metrics = [
        {
          id: 'y1',
          timeFormatValueId: 'hourMinute1',
          metricName: 'wf_metered',
        },
        {
          id: 'y2',
          timeFormatValueId: 'hourMinute2',
          metricName: 'wf_unmetered',
        },
      ];
      chart.tag = {
        name: 'data_chart_data_usage_time_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_wifi_time_on_metered_tooltip_value',
      };
      chart.legendDisabled = false;
      chart.legends = ['wf_metered', 'wf_unmetered'];
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
      chart.integerOnly = true;
    });
    patchCard<LineChartConfig>(page.charts, 'dwf-ssid-ts', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wifi_network_usage_by_device_ssid_icon_tooltip';
      chart.name = 'sdd_wifi_network_usage_by_device_ssid_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'CollectedTime',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'cnt',
        label: 'sdd_wifi_percentage_of_time_axis',
      };
      chart.tag = {
        name: 'wf_network_usage_devices_ssid_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_wifi_network_usage_by_device_ssid_tooltip_value',
      };
      chart.dynamicLegend = true;
      chart.filters = {
        topN: 5,
      };
      chart.uiSettings = [WF_SDD_CHART_UI_BAR_TOPN];
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
      chart.integerOnly = true;
      chart.formatTime = true;
    });
    patchCard<LineChartConfig>(page.charts, 'dwf-bssid-ts', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_network_usage_devices_ssid_icon_tooltip';
      chart.name = 'sdd_network_usage_devices_ssid_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'CollectedTime',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'cnt',
        label: 'sdd_wifi_percentage_of_time_axis',
      };
      chart.tag = {
        name: 'wf_network_usage_devices_ssid_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_wifi_network_usage_by_device_ssid_tooltip_value',
      };
      chart.dynamicLegend = true;
      chart.filters = {
        topN: 5,
      };
      chart.uiSettings = [WF_SDD_CHART_UI_BAR_TOPN];
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
      chart.formatTime = true;
    });

    patchCard<BarChartConfig>(page.charts, 'dwf-ssid', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wifi_network_usage_by_device_ssid_icon_tooltip';
      chart.name = 'sdd_wifi_network_ssid_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('sdd_wifi_network_usage_by_device_ssid_tooltip_label_reg', this.point.name)}`",
        labelArgs: ['sdd_wifi_network_usage_by_device_ssid_tooltip_label_reg', 'this.point.name'],
        value: "`${xsI18nPipe.transform('sdd_wifi_networks_ssid_most_used_tooltip_value', this.point.hourMinute)}`",
        valueArgs: ['sdd_wifi_networks_ssid_most_used_tooltip_value', 'this.point.hourMinute'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'wf_network_ssid_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_wifi_networks_ssid_most_used_metric_label',
      };
      chart.tag = {
        name: 'sdd_wifi_network_ssid_name',
        parameters: ['mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_SDD_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'dwf-bssid', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wifi_network_usage_by_device_bssid_icon_tooltip';
      chart.name = 'sdd_wifi_access_point_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.scrollableX = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('sdd_wifi_networks_bssid_most_used_tooltip_label', this.point.name)}`",
        labelArgs: ['sdd_wifi_networks_bssid_most_used_tooltip_label', 'this.point.name'],
        value: "`${xsI18nPipe.transform('sdd_wifi_networks_bssid_most_used_tooltip_value', this.point.hourMinute)}`",
        valueArgs: ['sdd_wifi_networks_bssid_most_used_tooltip_value', 'this.point.hourMinute'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'wf_access_points_bssid_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_wifi_network_usage_by_device_bssid_metric_label',
      };
      chart.tag = {
        name: 'sdd_wifi_access_point_name',
        parameters: ['mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_SDD_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'dwf-disconnect-bssid', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_wifi_access_point_disconnects_icon_tooltip';
      chart.allowPointSelect = false;
      chart.name = 'sdd_wifi_access_point_disconnects_name';
      chart.type = ChartTypeEnum.CYLINDER;
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('sdd_wifi_access_point_bssid_disconnects_most_least_tooltip_label', this.point.name)}`",
        labelArgs: ['sdd_wifi_access_point_bssid_disconnects_most_least_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('sdd_wifi_access_point_bssid_disconnects_most_least_tooltip_value', this.point.y)}`",
        valueArgs: ['sdd_wifi_access_point_bssid_disconnects_most_least_tooltip_value', 'this.point.y'],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'wf_access_points_bssid_disconnects_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_device_disconnects_most_metric_label',
      };
      chart.scrollableX = true;
      chart.inverted = true;
      chart.tag = {
        name: 'sdd_wifi_access_point_disconnects_name',
      };
      chart.marginOverride = 40;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.integerOnly = true;
      chart.uiSettings = [WF_SDD_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<LineChartConfig>(page.charts, 'dwf-disconnect-ts', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_number_of_device_disconnects_icon_tooltip';
      chart.name = 'sdd_number_of_device_disconnects_name';
      chart.type = ChartTypeEnum.LINE;
      chart.cardStyle = ['card-2x1'];
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_wifi_number_device_disconnects_tooltip_label_value',
      };
      chart.dimension = {
        dimensionId: 'CollectedTime',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_number_of_device_disconnects_metric_label',
      };
      chart.integerOnly = true;
      chart.legendDisabled = true;
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    page.applicationType = ['wifi'];
    page.icon = 'insight-icon-application';
    page.name = 'Wi-Fi';
    page.hourEnabled = true;
    page.hourSelectorType = HourSelectorType.CHECKBOX;
    page.predefinedCharts = [
      {
        category: {
          name: 'sdd_wf_predefined_chart_signal',
          icon: '',
          tooltip: 'sdd_wf_predefined_chart_signal_tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'dwf-signalstrength', 'sdd_wf_predefined_chart_signal_strength'),
          buildChartOptions(page.charts, 'dwf-freqband', 'sdd_wf_predefined_chart_freq_band'),
          buildChartOptions(page.charts, 'dwf-signalstrength-ts', 'sdd_wf_avg_signal_strength_name'),
        ],
      },
      {
        category: {
          name: 'sdd_wf_predefined_chart_connection',
          icon: '',
          tooltip: 'sdd_wf_predefined_chart_connection_tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'dwf-metered-ts', 'sdd_wf_predefined_chart_metered_unmetered_connections'),
        ],
      },
      {
        category: {
          name: 'sdd_wf_predefined_chart_usage',
          icon: '',
          tooltip: 'sdd_wf_predefined_chart_usage_tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'dwf-usage-updown', 'sdd_wifi_usage_up_down_name'),
          buildChartOptions(page.charts, 'dwf-usage-app', 'sdd_wifi_app_data_usage_name'),
          buildChartOptions(page.charts, 'dwf-usage-ts', 'sdd_data_chart_data_usage_time_fullname'),
          buildChartOptions(page.charts, 'dwf-ssid-ts', 'wf_predefined_network_usage_devices'),
          buildChartOptions(page.charts, 'dwf-bssid-ts', 'wf_predefined_access_point_usage_devices'),
          buildChartOptions(page.charts, 'dwf-ssid', 'wf_predefined_network_ssid_most'),
          buildChartOptions(page.charts, 'dwf-bssid', 'wf_predefined_access_points_bssid_most'),
        ],
      },
      {
        category: {
          name: 'sdd_wf_predefined_chart_disconnects',
          icon: '',
          tooltip: 'sdd_wf_predefined_chart_disconnects_tooltip',
        },
        charts: [
          buildChartOptions(
            page.charts,
            'dwf-disconnect-bssid',
            'sdd_wf_predefined_chart_access_points_bssid_disconnects'
          ),
          buildChartOptions(page.charts, 'dwf-disconnect-ts', 'sdd_wf_predefined_chart_number_of_device_disconnects'),
        ],
      },
    ];
  }
}

export function mapDeviceConfig(
  app: ApplicationConfigWithPages,
  otherApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.claims = [
    ProfilePermissionEnum.ProfileViewBatteryStatusDashboard,
    ProfilePermissionEnum.ProfileViewWifiDashboard,
    ProfilePermissionEnum.ProfileViewDataUsageDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ProfilePermissionEnum.ProfileViewLastLocationDashboard,
    ProfilePermissionEnum.ProfileViewSignalStrengthDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
    ProfilePermissionEnum.ProfileViewWebAppUsageDashboard,
  ];
  const pageIds = [
    findPageId(app.pages, DEVICE_LIST),
    //findPageId(app.pages, DEVICE_SUMMARY),
  ];
  const childPageIds = [
    findPageId(app.pages, DEVICE_BATTERY_USAGE),
    findPageId(app.pages, DEVICE_APP_USAGE),
    findPageId(app.pages, DEVICE_DATA_USAGE),
    findPageId(app.pages, DEVICE_WEBAPP_USAGE),
    findPageId(app.pages, DEVICE_LAST_KNOWN),
    findPageId(app.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(app.pages, DEVICE_WIFI),
    findPageId(otherApp.pages, SMART_BATTERY_SPOTLIGHT),
  ];
  app.name = 'Device_Filter_Device_List';
  patchPage(app, DEVICE_LIST, patchDeviceListPage);
  //patchPage(app, DEVICE_SUMMARY, patchDeviceSummaryPage);
  patchPage(app, DEVICE_BATTERY_USAGE, patchDeviceBatteryPage);
  patchPage(app, DEVICE_APP_USAGE, patchDeviceAppPage);
  patchPage(app, DEVICE_DATA_USAGE, patchDeviceDataPage);
  patchPage(app, DEVICE_WEBAPP_USAGE, patchDeviceWebAppPage);
  patchPage(app, DEVICE_SIGNAL_STRENGTH, sddLoc.patchDeviceSignalStrengthPage);
  patchPage(app, DEVICE_LAST_KNOWN, sddLoc.patchDeviceLastKnownPage);
  patchPage(app, DEVICE_WIFI, patchDeviceWifiPage);
  patchPage(otherApp, SMART_BATTERY_SPOTLIGHT, sbMapper.patchSmartBatterySpotlight);
  const defaultTabId = findPageId(app.pages, DEVICE_BATTERY_USAGE);
  app.pages.push(otherApp.pages.find(app => app?.baselineId === SMART_BATTERY_SPOTLIGHT));
  return { ...app, childPageIds, pageIds, defaultTabId };
}
